import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Badge,
  Button,
  CondensedHeader,
  CondensedLarge,
  CondensedMedium,
  ContextMenu,
  ContextMenuItem,
  Flashy,
  Gutter,
  IconButton,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Paragraph,
  SectionHeader,
  SmallHeader,
} from '@axiom/ui';
import { AccountConst } from '@axiom/const';

import StoreStateTreatment from '../StoreStateTreatment/StoreStateTreatment';
import { FindContactForAccountModal } from '../FindContactForAccountModal/FindContactForAccountModal';
import { AccountStore } from '../../stores/account-store';
import { PreloadedUserStore } from '../../stores/preloaded-user-store';
import SearchInput from '../SearchInput/SearchInput';
import { DateUtil } from '../../utils/date-util';
import {
  generateBenchUrl,
  generateAFCLandingPageUrl,
} from '../../utils/link-util';
import { isEnvoyAdmin } from '../../utils/roles-util';
import { ManageAccountsConfidential } from '../ManageAccountsConfidential/ManageAccountsConfidential';
import { ManageAccountEmails } from '../ManageAccountEmails/ManageAccountEmails';

import {
  ManageAccountsFormWrapper,
  ManageAccountsAccountDetails,
  ManageAccountsAssociatedUsersSearchRow,
  ManageAccountsColumn,
  ManageAccountsContactName,
  ManageAccountsEmail,
  ManageAccountsEmailColumn,
  ManageAccountsEmpty,
  ManageAccountsFormHeader,
  ManageAccountsRow,
} from './ManageAccountsFormStyles';

const ManageAccountsFormComponent = ({ accountState, user }) => {
  const [confirmContactId, setConfirmContactId] = useState(null);
  const [showFindContactModal, setShowFindContactModal] = useState(false);
  const [query, setQuery] = useState('');

  const handleQueryChange = e => setQuery(e.target.value);

  const queryRegExp = new RegExp(query, 'i');

  const filterContacts = (contacts, q) =>
    q
      ? contacts.filter(({ firstName, lastName }) =>
          queryRegExp.test(`${firstName} ${lastName}`)
        )
      : contacts;

  useEffect(() => {
    setQuery('');
  }, [accountState]);

  return (
    <StoreStateTreatment
      storeState={accountState}
      name="MANAGE_ACCOUNT_FORM"
      renderLoadedView={({ data }) => {
        const {
          contacts,
          name,
          calculatedName,
          parentAccount,
          id: accountId,
          accountType,
          denied,
        } = data;
        return (
          <ManageAccountsFormWrapper>
            <ManageAccountsFormHeader>
              <SmallHeader name="CLIENT_NAME">{name}</SmallHeader>

              <ContextMenu
                name="OPPORTUNITY_CONTEXT_MENU_MANAGE_ACCOUNT"
                direction="left"
              >
                <ContextMenuItem
                  onClick={() => {
                    window.open(generateAFCLandingPageUrl(accountId));
                  }}
                >
                  Open AFC Homepage
                </ContextMenuItem>
                <ContextMenuItem
                  onClick={() => {
                    window.open(generateBenchUrl(accountId));
                  }}
                >
                  Open AFC Bench
                </ContextMenuItem>
                <ContextMenuItem
                  onClick={() => {
                    setShowFindContactModal(true);
                  }}
                >
                  Add associated user
                </ContextMenuItem>
              </ContextMenu>

              <ManageAccountsAccountDetails>
                <div>
                  <dt>Account Display Name</dt>
                  <dd data-test="ACCOUNT_DISPLAY_NAME">
                    {calculatedName || 'None'}
                  </dd>
                </div>
                <div>
                  <dt>Parent Account</dt>
                  <dd data-test="PARENT_ACCOUNT_NAME">
                    {parentAccount?.calculatedName || 'None'}
                  </dd>
                </div>
                <Gutter bottom="10px" />
                {accountType === AccountConst.Types.Direct &&
                  (denied ? (
                    <Badge background="red">Denied</Badge>
                  ) : (
                    <Badge background="navy blue">Self Service</Badge>
                  ))}
              </ManageAccountsAccountDetails>
            </ManageAccountsFormHeader>
            {isEnvoyAdmin(user) && (
              <ManageAccountsConfidential account={data} />
            )}
            <ManageAccountsRow divider>
              <ManageAccountsColumn>
                <CondensedHeader>
                  User Email Notification Preferences
                </CondensedHeader>
              </ManageAccountsColumn>
              <ManageAccountsColumn>
                <ManageAccountEmails account={data} />
              </ManageAccountsColumn>
            </ManageAccountsRow>
            <ManageAccountsAssociatedUsersSearchRow divider>
              <ManageAccountsColumn>
                <Gutter vertical="2rem">
                  <SectionHeader>Associated Users</SectionHeader>
                </Gutter>
              </ManageAccountsColumn>
              <ManageAccountsColumn>
                <SearchInput
                  data-test="ACCOUNT_CONTACT_SEARCH"
                  onChange={handleQueryChange}
                  placeholder="Search Users"
                  value={query}
                />
              </ManageAccountsColumn>
            </ManageAccountsAssociatedUsersSearchRow>
            {filterContacts(contacts, query).map(
              ({
                firstName,
                id: contactId,
                lastName,
                email,
                user: contactUser,
              }) => (
                <ManageAccountsRow
                  key={contactId}
                  data-test="ASSOCIATED_CONTACT"
                  divider
                >
                  <ManageAccountsColumn>
                    <ManageAccountsContactName data-test="CONTACT_NAME">
                      {`${firstName} ${lastName}`}
                    </ManageAccountsContactName>
                    <CondensedMedium>
                      <Flashy italic>
                        Last Sign-In:{' '}
                        <span data-test="LAST_SIGN_IN">
                          {DateUtil.displayDate(contactUser?.lastLoginAt) ||
                            'Never'}
                        </span>
                      </Flashy>
                    </CondensedMedium>
                  </ManageAccountsColumn>
                  <ManageAccountsColumn>
                    <ManageAccountsEmailColumn>
                      <ManageAccountsEmail data-test="EMAIL">
                        {email}
                      </ManageAccountsEmail>
                      <IconButton
                        icon="trash"
                        pattern="secondary"
                        variation="minimal"
                        title={`Remove ${firstName} ${lastName}`}
                        onClick={() => setConfirmContactId(contactId)}
                      />
                    </ManageAccountsEmailColumn>
                  </ManageAccountsColumn>
                </ManageAccountsRow>
              )
            )}
            {!!confirmContactId && (
              <Modal size="confirm">
                <ModalHeader onClose={() => setConfirmContactId(null)}>
                  Remove Contact
                </ModalHeader>
                <ModalSection>
                  <Paragraph>
                    Are you sure you would like to remove this contact from this
                    account?
                  </Paragraph>
                </ModalSection>
                <ModalFooter>
                  <Button
                    variation="outline"
                    onClick={() => setConfirmContactId(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (confirmContactId) {
                        AccountStore.removeContactFromAccount(
                          confirmContactId,
                          accountId
                        );
                        setConfirmContactId(null);
                      }
                    }}
                  >
                    Remove
                  </Button>
                </ModalFooter>
              </Modal>
            )}
            {showFindContactModal ? (
              <FindContactForAccountModal
                accountId={accountId}
                onClose={() => {
                  setShowFindContactModal(false);
                }}
                onSuccess={() => {
                  AccountStore.load(accountId);
                }}
              />
            ) : null}
          </ManageAccountsFormWrapper>
        );
      }}
      renderNonLoadedView={() => {
        return (
          <ManageAccountsEmpty>
            <CondensedLarge>Select a search result.</CondensedLarge>
          </ManageAccountsEmpty>
        );
      }}
    />
  );
};

ManageAccountsFormComponent.propTypes = {
  accountState: AccountStore.getDataShape().isRequired,
  user: PreloadedUserStore.getDataShape().isRequired,
};

export const ManageAccountsForm = connect(
  state => ({
    accountState: AccountStore.select(state),
    user: PreloadedUserStore.select(state),
  }),
  {}
)(ManageAccountsFormComponent);
